import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import ScrollingTabNav from 'components/Tabs/ScrollingTabNav'
import TabItem from 'components/Tabs/TabItem'
import TabPane from 'components/Tabs/TabPane'
import { useHashTab } from 'components/Tabs/useHashTab'
import useFlag from 'hooks/useFlag'
import { useReportPermission } from 'hooks/useReportPermission'
import { buildTranslate } from 'locales'
import React from 'react'
import { Container } from 'react-bootstrap-five'
import Tab from 'react-bootstrap-five/Tab'
import Helmet from 'react-helmet'
import { Link } from 'react-router-dom'
import { history } from 'store'
import { useAbility } from 'utilities/ability'
import Content from './Content'
import TopFiftyCustomersTab from './TopFiftyCustomersTab'

const t = buildTranslate('users.user_list')

enum Tabs {
  ALL_CUSTOMERS = 'all_customers',
  TOP_50_CUSTOMERS = 'top_50_customers',
}

const Landing: React.FC = () => {
  const [css] = useStyletron()
  const ability = useAbility()
  const canImportEmailSubscribers = ability.can('manage', 'LegacyReports')
  const userListsEnabled = useFlag('self-serve-list-upload', false)
  const canAccessUserLists =
    userListsEnabled && ability.can('access', 'UserLists')
  const promoteInsightsLabReportsEnabled = useFlag(
    'mx-promote-insights-lab-reports',
    false
  )
  // "Top 50 customers" is a report-grade Looker embed, so it needs the same
  // permission a report page applies. The page itself must stay open to any
  // authenticated user — /users is the customer list, not a report — so the
  // check gates the tab, not the page. CustomerReport is the closest
  // analogue (customer-level, not location-scoped), so mirror its subjects
  // rather than the LocationReports pair the rest of this chain uses.
  const topFiftyPermission = useReportPermission({
    rbac: 'Reports',
    legacy: 'Insights',
    legacyAction: 'manage',
  })
  const canAccessTopFiftyCustomers = ability.can(
    topFiftyPermission.action,
    topFiftyPermission.resource
  )
  // With the tab hidden there is only one tab left, so fall back to master's
  // bare customer list rather than rendering a one-item tab bar.
  const showTabs =
    promoteInsightsLabReportsEnabled && canAccessTopFiftyCustomers
  // Only the flag-on path renders tabs, so the hash is resolved against the
  // tabs actually on screen; with the tab hidden the set is just the default.
  const visibleTabs = showTabs
    ? [Tabs.ALL_CUSTOMERS, Tabs.TOP_50_CUSTOMERS]
    : [Tabs.ALL_CUSTOMERS]
  const [activeTab, setActiveTab] = useHashTab(visibleTabs, Tabs.ALL_CUSTOMERS)

  const handleSwitchTab = (tab: Tabs) => {
    history.replace({ ...history.location, hash: tab })
    setActiveTab(tab)
  }

  return (
    <div className="w-100 h-100 pt-m pb-xxl px-0 white-bkg border-bottom-1 grey-20-border">
      <Container className="pt-m pb-xl">
        <div className="d-flex justify-content-between">
          <Text.Header2 className="m-0 mr-xl">{t('title')}</Text.Header2>
          <div>
            {canAccessUserLists && (
              <Link to="/user_lists" className="btn btn-primary btn-lg">
                {t('import_customers')}
              </Link>
            )}
            {!canAccessUserLists && canImportEmailSubscribers && (
              <Link
                to="/subscribers/setup/connect"
                className="btn btn-primary btn-lg"
              >
                {t('import_email_subscribers')}
              </Link>
            )}
          </div>
        </div>
      </Container>
      <Helmet>
        <title>{t('page_title')}</title>
      </Helmet>
      {showTabs ? (
        <Tab.Container
          activeKey={activeTab}
          transition={false}
          onSelect={(tab: string | null) => {
            if (!tab) return
            handleSwitchTab(tab as Tabs)
          }}
        >
          {/* Only the flag-on path has a tab bar at all — with the flag off
              this whole Tab.Container is not rendered and the page keeps
              master's bare customer list, so there is no flag-off nav to
              preserve here. */}
          <ScrollingTabNav ariaLabel={t('title')}>
            <TabItem
              state={Tabs.ALL_CUSTOMERS}
              title={t('tabs.all_customers.title')}
            />
            <TabItem
              state={Tabs.TOP_50_CUSTOMERS}
              title={t('tabs.top_50_customers.title')}
            />
          </ScrollingTabNav>
          <Tab.Content
            className={`pb-xxl h-100 grey-05-bkg ${css({
              minHeight: 'calc(100% - 75px)',
            })}`}
          >
            <TabPane state={Tabs.ALL_CUSTOMERS}>
              <Content />
            </TabPane>
            {/* mountOnEnter: Tab.Container runs with transition={false}, so
                without it this pane's Looker embed_url fetch and iframe fire
                on every /users page load even though nobody opened the tab. */}
            <TabPane state={Tabs.TOP_50_CUSTOMERS} mountOnEnter>
              <TopFiftyCustomersTab />
            </TabPane>
          </Tab.Content>
        </Tab.Container>
      ) : (
        <div
          className={`grey-05-bkg border-top-1 pb-xxl grey-20-border pt-xl ${css(
            {
              minHeight: 'calc(100% - 75px)',
            }
          )}`}
        >
          <Container>
            <Content />
          </Container>
        </div>
      )}
    </div>
  )
}

export default Landing
